import type { KeyboardEvent } from "react";

import { Checkbox, Select, TextField } from "@radix-ui/themes";

import type {
  LayoutSettingChange,
  LayoutSettingControl,
  LayoutSettingFieldView,
} from "./layoutSettings.types";

export interface ControlProps {
  field: LayoutSettingFieldView;
  controlId: string;
  descId: string;
  errorId: string;
  onFieldChange: (fieldId: string, change: LayoutSettingChange) => void;
}

export const buildDescribedBy = (
  descId: string,
  errorId: string,
  hasError: boolean,
): string => (hasError ? `${descId} ${errorId}` : descId);

/** Enter means "apply now" for every debounced draft input. */
export const isCommitKey = (event: KeyboardEvent<HTMLInputElement>) =>
  event.key === "Enter";

export const BooleanControlPresentation = ({
  field,
  controlId,
  descId,
  errorId,
  onFieldChange,
}: ControlProps) => {
  const hasError = !!field.error;
  return (
    <Checkbox
      id={controlId}
      size="1"
      checked={field.value === true}
      disabled={field.disabled}
      aria-describedby={buildDescribedBy(descId, errorId, hasError)}
      aria-invalid={hasError || undefined}
      onCheckedChange={(checked) =>
        onFieldChange(field.descriptor.id, {
          kind: "boolean",
          value: checked === true,
        })
      }
    />
  );
};

export const DraftControlPresentation = ({
  field,
  controlId,
  descId,
  errorId,
  onFieldChange,
  numeric,
  ariaLabel,
}: ControlProps & { numeric: boolean; ariaLabel?: string }) => {
  const hasError = !!field.error;
  const commit = () =>
    onFieldChange(field.descriptor.id, { kind: "draft-commit" });
  return (
    <TextField.Root
      id={controlId}
      size="1"
      variant="soft"
      inputMode={numeric ? "decimal" : undefined}
      value={String(field.value)}
      disabled={field.disabled}
      aria-label={ariaLabel}
      aria-describedby={buildDescribedBy(descId, errorId, hasError)}
      aria-invalid={hasError || undefined}
      onChange={(e) =>
        onFieldChange(field.descriptor.id, {
          kind: "draft",
          value: e.target.value,
        })
      }
      onKeyDown={(e) => {
        if (isCommitKey(e)) {
          commit();
        }
      }}
      onBlur={commit}
    />
  );
};

interface SelectControlProps extends ControlProps {
  control: Extract<LayoutSettingControl, { kind: "select" }>;
}

export const SelectControlPresentation = ({
  field,
  controlId,
  descId,
  errorId,
  control,
  onFieldChange,
}: SelectControlProps) => {
  const hasError = !!field.error;
  // Radix Select rejects an empty string as `value` (it reserves ""), and a
  // literal `"undefined"` from `String(undefined)` is nonsense to render.
  // Coerce the field's value to a defined non-empty string, or fall back
  // to the uncontrolled (undefined) state so Radix picks the first option.
  const selectValue =
    field.value === undefined || field.value === null
      ? undefined
      : String(field.value) || undefined;
  return (
    <Select.Root
      value={selectValue}
      disabled={field.disabled}
      onValueChange={(value) =>
        onFieldChange(field.descriptor.id, {
          kind: "select",
          value,
        })
      }
    >
      <Select.Trigger
        id={controlId}
        variant="soft"
        aria-describedby={buildDescribedBy(descId, errorId, hasError)}
        aria-invalid={hasError || undefined}
      />
      <Select.Content variant="soft">
        {control.options.map((opt) => (
          <Select.Item key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
};
